import React, { useEffect, useState } from "react";
import { Input, Button, Tooltip, message } from "antd";
import { AudioOutlined, AudioMutedOutlined } from "@ant-design/icons";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { Question, QuestionAnswers } from "../../../../Interface/Index";
import "./descriptiveQuestion.scss";
import { useInterviewRoundContext } from "../../../../Context/InterviewRoundContext";

interface DescriptiveQuestionProps {
  question: Question;
  questionAnswer: QuestionAnswers;
  isRoundFinished: boolean;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

const DescriptiveQuestion: React.FC<DescriptiveQuestionProps> = ({
  question,
  isRoundFinished,
  questionAnswer,
  onChange,
}) => {
  const { elementRef } = useInterviewRoundContext();

  message.config({
    getContainer: () =>
      elementRef?.current ? elementRef.current : document.body,
  });

  const isDisabled =
    (question.responses && question.responses.length > 0) || isRoundFinished;

  const getInitialAnswer = () => {
    return isDisabled
      ? question?.responses?.[0]?.description ?? ""
      : questionAnswer?.answer ?? "";
  };

  const [typedAnswer, setTypedAnswer] = useState<string>(getInitialAnswer());
  const [finalAnswer, setFinalAnswer] = useState<string>(getInitialAnswer());

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  useEffect(() => {
    const initial = getInitialAnswer();
    setTypedAnswer(initial);
    setFinalAnswer(initial);
    resetTranscript();
  }, [question?._id]);

  const combinedAnswer = listening
    ? `${typedAnswer} ${transcript}`.trim()
    : finalAnswer;

  useEffect(() => {
    if (listening) {
      updateParentState(combinedAnswer);
    }
  }, [transcript]);

  const updateParentState = (value: string) => {
    const syntheticEvent = {
      target: { value },
    } as React.ChangeEvent<HTMLTextAreaElement>;
    onChange(syntheticEvent);
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const value = (e.target as HTMLTextAreaElement).value;
    setTypedAnswer(value);
    setFinalAnswer(value);
    updateParentState(value);
  };

  const handleBlockedAction = (e: any) => {
    e.preventDefault();
    message.warning("Copy, paste, and cut actions are disabled.");
  };

  const handleStartListening = () => {
    if (!browserSupportsSpeechRecognition) {
      message.error("Speech recognition not supported in this browser.");
      return;
    }
    resetTranscript();
    SpeechRecognition.startListening({ continuous: true, language: "en-US" });
  };

  const handleStopListening = () => {
    SpeechRecognition.stopListening();
    const merged = `${typedAnswer} ${transcript}`.trim();
    setTypedAnswer(merged);
    setFinalAnswer(merged);
    updateParentState(merged);
    resetTranscript();
  };

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  return (
    <div className="descriptive-question-container">
      {!isDisabled && !isSafari && (
        <div
          style={{ marginBottom: 8, display: "flex", justifyContent: "flex-end" }}
        >
          <Tooltip title={listening ? "Stop listening" : "Speak your answer"}>
            <Button
              type="primary"
              icon={listening ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={listening ? handleStopListening : handleStartListening}
              size="small"
            >
              {listening ? "Listening..." : "Speak"}
            </Button>
          </Tooltip>
        </div>
      )}
      <Input.TextArea
        rows={6}
        placeholder="Type your answer here"
        value={combinedAnswer}
        onInput={handleInput}
        disabled={isDisabled}
        onCopy={handleBlockedAction}
        onPaste={handleBlockedAction}
        onCut={handleBlockedAction}
      />
    </div>
  );
};

export default DescriptiveQuestion;
